import { RiInformationFill } from "@remixicon/react";
import React from "react";
import {
    ChannelMode, ChannelType, GetSettingsDocument,
    IntegrationActionType, SettingsChannelSlack,
    useSaveSettingsMutation,
} from "../../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../../api";
import Editor from "../../../../../components/agent-v3/editor/Editor";
import { useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import AdvancedSection from "../../../../../components/expanding-section/AdvancedSection";
import ChannelModePicker from "../../../../../components/pickers/channel-mode/ChannelMode";
import IconUploader from "../../../../../components/pickers/icon-uploader/IconUploader";
import Integrations from "../../../../../components/pickers/integrations/Integrations";
import RequestTypes from "../../../../../components/pickers/request-types/RequestTypes";
import SlackBots from "../../../../../components/pickers/slack-bots/SlackBots";
import TagList from "../../../../../components/tag-list/TagList";
import Alert from "../../../../../components/theme/alert/Alert";
import FormGroup from "../../../../../components/theme/form-group/FormGroup";
import SettingsContainer from "../../../../../components/theme/settings";
import Toggle from "../../../../../components/theme/toggle/Toggle";
import { NavLink, Paths } from "../../../../../contexts/Routing";
import { DEEP_COMPARER, SettingsProps, SOURCES } from "../../base";
import { TICKET_CREATED_MESSAGE_VARIABLES, WELCOME_VARIABLES } from "../../variables";
import ChannelModeHelp from "../slack/ChannelModeHelp";
import Styles from "./ChannelSettings.module.scss";

const SLACK_TITLE = (
    <>
        {SOURCES[ChannelType.Slack].mediumIcon}
        {" "}
        Slack Settings Configuration
    </>
);
export default function ChannelSettings(props: SettingsProps) {
    const [save] = useSaveSettingsMutation();

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .property("appName", () => props.settings?.channelSlack?.name ?? "")
            .property("appIcon", () => props.settings?.channelSlack?.iconUrl ?? undefined)

            .object("introduction", () => props.settings?.channelSlack?.introduction ?? {}, DEEP_COMPARER)
            .object("offHoursIntroduction", () => props.settings?.channelSlack?.offHoursIntroduction ?? {}, DEEP_COMPARER)

            .array("identities", () => props.settings?.identities ?? [])
            .property("channelMode", () => props.settings?.channelSlack?.channelMode ?? ChannelMode.Threaded)
            .property("canFillOutForm", () => props.settings?.channelSlack?.canFillOutForm ?? true)
            .array("approvedBotIds", () => props.settings?.channelSlack?.approvedBotIds ?? [])
            .array("ignoreTicketsFromEmailDomains", () => props.settings?.channelSlack?.ignoreTicketsFromEmailDomains ?? [])

            .property("ticketCreatedTemplate", () => props.settings?.channelSlack?.ticketCreatedTemplate ?? "")

            .array("slackRequestTypes", () => props.settings?.channelSlack?.formIds ?? [])
            .array("slackMessageActionFormIds", () => props.settings?.channelSlack?.messageActionFormIds ?? [])
            .property("slackRequestFormText", () => props.settings?.channelSlack?.requestFormText ?? "")
            .property("slackShouldDisplayAgentJoin", () => props.settings?.channelSlack?.shouldDisplayAgentJoin ?? false)
            .property("slackAllowAddParticipants", () => props.settings?.channelSlack?.shouldAllowUsersAddParticipants ?? false),

        [props.settings],
    );

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "Channel Settings Saved.",
        onSave: async (__, onDone) => {
            await save({
                refetchQueries: [
                    { query: GetSettingsDocument },
                ],
                variables: {
                    identities: properties.identities.value,
                    channelSlack: cloneWithoutTypename<SettingsChannelSlack>({
                        name: properties.appName.value,
                        iconUrl: properties.appIcon.value,
                        introduction: properties.introduction.value,
                        offHoursIntroduction: properties.offHoursIntroduction.value,

                        shouldDisplayAgentJoin: properties.slackShouldDisplayAgentJoin.value,
                        ignoreTicketsFromEmailDomains: properties.ignoreTicketsFromEmailDomains.value,
                        channelMode: properties.channelMode.value,
                        formIds: properties.slackRequestTypes.value,
                        messageActionFormIds: properties.slackMessageActionFormIds.value,
                        approvedBotIds: properties.approvedBotIds.value,
                        requestFormText: properties.slackRequestFormText.value,
                        shouldAllowUsersAddParticipants: properties.slackAllowAddParticipants.value,
                        ticketCreatedTemplate: properties.ticketCreatedTemplate.value,
                        canFillOutForm: properties.canFillOutForm.value,
                    }),
                },
            });
            onDone();
        },
    });

    return (
        <SettingsContainer navigation="tabs" saveElement={saveElement}>
            <SettingsContainer.Section
                title="Global Channel Settings"
                helpText="These general settings help you manage this specific channel and its overrides"
            >
                <SettingsContainer as="div">
                    <SettingsContainer.Section
                        title="Brand"
                        helpText="General settings which apply to all applications."
                    >
                        <SettingsContainer.Setting
                            title="Application Icon"
                            description="The default icon the bot will use. This usually appears in
                                automated responses. Max image size is 20 MBs"
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={props.isLoading}
                        >
                            <IconUploader
                                value={properties.appIcon.value}
                                onChange={properties.appIcon.setValue}
                                shouldStore
                            />
                        </SettingsContainer.Setting>
                        <SettingsContainer.Setting
                            title="Application Name"
                            description="The default application name the bot will use. This
                                usually appears for automated responses"
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={props.isLoading}
                        >
                            <FormGroup
                                value={properties.appName.value}
                                onChange={properties.appName.setValueFromInput}
                                type="text"
                                size="small"
                            />
                        </SettingsContainer.Setting>
                    </SettingsContainer.Section>
                    <SettingsContainer.Section
                        title="Welcome Messages"
                        helpText={`These messages are automatically sent to the user when a new chat is
                                requested. You can display a different message if you are out of
                                office with the "Off-Hours Introduction Message". The hours of operations
                                are managed in Time settings.`}
                    >
                        <SettingsContainer.Setting
                            title="First Response Message"
                            description="When a customer first asks a question, this is the message they will first see.
                                To make the message more helpful, you can use variables (x)."
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={props.isLoading}
                        >
                            <Editor
                                value={properties.introduction.value?.rawText ?? ""}
                                onChange={rawText => properties.introduction.setValue({ rawText })}
                                size="medium"
                                hasBorder
                                placeholder="Hey, please remember to use a thread..."
                                variables={WELCOME_VARIABLES}
                            />
                        </SettingsContainer.Setting>
                        <SettingsContainer.Setting
                            title="Off-hours First Response Message"
                            description="Off Hours are controlled by hours of operation settings below. If no message is
                                specified, Agent will display the default introduction message. To make the message
                                more helpful, you can use variables (x)."
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={props.isLoading}
                        >
                            <Editor
                                value={properties.offHoursIntroduction.value?.rawText ?? ""}
                                onChange={rawText => properties.offHoursIntroduction.setValue({ rawText })}
                                size="medium"
                                hasBorder
                                placeholder="Hey, please remember to use a thread..."
                                variables={WELCOME_VARIABLES}
                            />
                        </SettingsContainer.Setting>
                    </SettingsContainer.Section>
                    <SettingsContainer.Section
                        title="Channel Defaults"
                        helpText="The following settings are the default, global configuration for many global
                            channel settings. Many of the settings here can be overriden by either the channel
                            or channel group settings."
                    >
                        <SettingsContainer.Setting
                            title="Channel Mode"
                            description="When a user types a messages in a channel, should that create a new
                            ticket and thread, or should ticket creation be done manually using features such
                            as emoji actions or the channel menu?"
                            descriptionElement={(
                                <>
                                    When a user types a messages in a channel, should that create a new
                                    ticket and thread, or should ticket creation be done manually using features such
                                    as emoji actions or the channel menu?
                                    {" "}
                                    <ChannelModeHelp iconSize="xs" iconVariant="default" />
                                </>
                            )}
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={props.isLoading}
                        >
                            <ChannelModePicker
                                value={properties.channelMode.value}
                                onChange={properties.channelMode.setValue}
                                className={SettingsContainer.Setting.PickerClassName}
                            />

                        </SettingsContainer.Setting>
                        <AdvancedSection title="Advanced Settings">
                            <SettingsContainer.Setting
                                title="Ticket Integration"
                                description="Which integration should create a ticket"
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <Integrations
                                    placeholder="Which integration should create a ticket"
                                    onSelected={selected => {
                                        properties.identities.setValue(selected ? [selected] : []);
                                    }}
                                    className={SettingsContainer.Setting.PickerClassName}
                                    canSetNone
                                    value={properties.identities.value.find(
                                        identity => identity.startsWith("urn:fq:integration"),
                                    )}
                                    actions={[IntegrationActionType.CreateTicket]}
                                />
                            </SettingsContainer.Setting>
                            <SettingsContainer.Setting
                                title="Ticket Created Message Template"
                                description="When a ticket is created and a new thread is posted in the channel,
                                    what information should this ticket contain?To make the message
                                    more helpful, you can use variables (x)."
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <Editor
                                    value={properties.ticketCreatedTemplate.value}
                                    onChange={properties.ticketCreatedTemplate.setValue}
                                    size="medium"
                                    hasBorder
                                    placeholder={"New Request Created: {{SUBJECT}}\n{{DESCRIPTION}}\nCreated By: {{CREATOR}}"}
                                    variables={TICKET_CREATED_MESSAGE_VARIABLES}
                                />
                            </SettingsContainer.Setting>
                            <SettingsContainer.Setting
                                title="Ignore Internal Messages"
                                description="If your team frequently sends content in customer
                                    channels, and you don&apos;t want tickets created for these, add their email or domain
                                    (e.g., @example.com) here. This will prevent ticket creation for matching users,
                                    overriding the general configuration."
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <TagList
                                    placeholder="@example.com"
                                    value={properties.ignoreTicketsFromEmailDomains.value}
                                    onChange={properties.ignoreTicketsFromEmailDomains.setValue}
                                    canNotHaveDuplicates
                                />
                            </SettingsContainer.Setting>
                            <SettingsContainer.Setting
                                title="Fill out form prompt"
                                description={`When creating a ticket, if a request type is set for the channel,
                                    users will see a "Fill out form" button for more details. To hide this option
                                    in a channel menu, turn this setting OFF.`}
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <Toggle
                                    checked={properties.canFillOutForm.value}
                                    onChange={properties.canFillOutForm.setCheckedFromInput}
                                />
                            </SettingsContainer.Setting>
                            <SettingsContainer.Setting
                                title="Approved Bots"
                                description={`By default, Foqal Agent blocks messages from other Slack bots. You can
                                        override this to allow Agent to inject messages and create tickets
                                        from bot messages in your workspace.`}
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <SlackBots
                                    isMulti
                                    value={properties.approvedBotIds.value}
                                    onSelected={properties.approvedBotIds.setValue}
                                />
                            </SettingsContainer.Setting>

                        </AdvancedSection>
                    </SettingsContainer.Section>
                </SettingsContainer>

            </SettingsContainer.Section>
            <SettingsContainer.Section
                title={SLACK_TITLE}
                helpText="These settings control certain Foqal Agent behaviors in Slack."
            >
                <SettingsContainer navigation="none" as="div">
                    <SettingsContainer.Section
                        title="Chat Settings"
                        helpText="These settings control certain Foqal Agent behaviors in Slack channels."
                    >
                        <SettingsContainer.Setting
                            title="Request Types"
                            description="When a user asks a question, Agent will
                                display a set of request types for the user to fill out.
                                To create a new request type or edit existing ones, view
                                Request types. To order the request types, click and
                                drag them inside this box."
                            permissions={SettingsContainer.Setting.Permissions.Administrator}
                            isLoading={props.isLoading}
                        >
                            <RequestTypes
                                isMulti
                                onSelected={properties.slackRequestTypes.setValue}
                                value={properties.slackRequestTypes.value}
                            />
                        </SettingsContainer.Setting>
                        <AdvancedSection title="Advanced Settings">
                            <SettingsContainer.Setting
                                title="Message Action Request Types"
                                description="This setting controls which request types are able to be
                                created by users when users hover over any message and select
                                Create Ticket. To order the request types, click and drag them
                                inside this box."
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <RequestTypes
                                    isMulti
                                    onSelected={properties.slackMessageActionFormIds.setValue}
                                    value={properties.slackMessageActionFormIds.value}
                                />
                            </SettingsContainer.Setting>

                            <SettingsContainer.Setting
                                title="Request form Text"
                                description="When requesting a user to fill out a request type, these instructions
                                    are provided to give additional context why filling out the form is helpful or necessary."
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <Editor
                                    value={properties.slackRequestFormText.value}
                                    onChange={properties.slackRequestFormText.setValue}
                                    size="medium"
                                    hasBorder
                                    placeholder="Instructions..."
                                />
                            </SettingsContainer.Setting>

                            <SettingsContainer.Setting
                                title="Show join/leave notifications"
                                description={`Foqal Agent can display a message to the requester when an
                                    agent joins or leaves the channel. This can be helpful for the requester
                                    to know when and agent begins working on their case.`}
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <Toggle
                                    checked={properties.slackShouldDisplayAgentJoin.value}
                                    onChange={properties.slackShouldDisplayAgentJoin.setCheckedFromInput}
                                />
                            </SettingsContainer.Setting>

                            <SettingsContainer.Setting
                                title="Allow users to add participants"
                                description={`If allowed, users will be able to @mention other
                                    users and add them as participants to the conversation.`}
                                permissions={SettingsContainer.Setting.Permissions.Administrator}
                                isLoading={props.isLoading}
                            >
                                <Toggle
                                    checked={properties.slackAllowAddParticipants.value}
                                    onChange={properties.slackAllowAddParticipants.setCheckedFromInput}
                                />
                            </SettingsContainer.Setting>
                        </AdvancedSection>
                        <Alert prefixIcon={<RiInformationFill />} variant="primary" className={Styles.alert}>
                            To view and manage channels go to the
                            {" "}
                            <NavLink
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channels" }}
                                variant="none"
                            >
                                Channels List
                            </NavLink>
                            .
                        </Alert>
                    </SettingsContainer.Section>
                </SettingsContainer>
            </SettingsContainer.Section>
        </SettingsContainer>
    );
}
